import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { 
  MapPin, Gauge, Fuel, Calendar, Eye, User, Mail, Phone,
  ChevronLeft, ChevronRight, Share2, Heart, Shield, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const CarDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const response = await axios.get(`${API}/cars/${id}`);
        setCar(response.data);
      } catch (error) {
        console.error('Error fetching car:', error);
        toast.error(t('detail.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
    // Check if favorited
    if (user || token) {
      axios.get(`${API}/favorites/ids`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(r => { if (r.data.includes(id)) setIsFavorite(true); }).catch(() => {});
    }
  }, [id, token, user, t]);

  const toggleFavorite = async () => {
    if (!user && !token) { toast.error(t('detail.loginFav')); return; }
    try {
      const r = await axios.post(`${API}/favorites/${id}`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setIsFavorite(r.data.favorited);
      toast.success(r.data.favorited ? t('detail.addedFav') : t('detail.removedFav'));
    } catch { }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t('common.linkCopied'));
  };

  const handleContact = async () => {
    if (!user && !token) { toast.error(t('detail.loginContact')); navigate('/login'); return; }
    if (car.seller_id === user?.id) { toast.info(t('detail.yourListing')); return; }
    try {
      await axios.post(`${API}/conversations`, { user_id: car.seller_id, car_id: car.id }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      navigate('/dashboard/messages');
      toast.success(t('detail.convOpened'));
    } catch (e) { toast.error(e.response?.data?.detail || t('detail.convFailed')); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold mb-4">{t('detail.carNotFound')}</h2>
          <Link to="/cars">
            <Button className="bg-cyan-500 text-black hover:bg-cyan-400">
              {t('detail.browseCars')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = car.images?.length > 0 ? car.images.map(img => 
    img.startsWith('/api/') ? `${API.replace('/api', '')}${img}` : img
  ) : [
    'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=1200'
  ];

  const specs = [
    { label: t('detail.specYear'), value: car.year, icon: Calendar },
    { label: t('detail.specMileage'), value: `${formatMileage(car.mileage)} km`, icon: Gauge },
    { label: t('detail.specFuel'), value: car.fuel_type, icon: Fuel },
    { label: t('detail.specTransmission'), value: car.transmission, icon: null },
    { label: t('detail.specBodyType'), value: car.body_type, icon: null },
    { label: t('detail.specColor'), value: car.color, icon: null },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/cars" className="hover:text-cyan-500 transition-colors">Cars</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/cars?brand=${car.brand}`} className="hover:text-cyan-500 transition-colors">{car.brand}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{car.model}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative rounded-2xl overflow-hidden glass-card">
              <div className="aspect-[16/10] relative">
                <img
                  src={images[currentImageIndex]}
                  alt={car.title}
                  className="w-full h-full object-cover car-image-stylized"
                />
                
                {/* Condition Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg ${car.condition === 'new' ? 'bg-green-500 text-black' : 'bg-white/20 text-white backdrop-blur-sm'}`}>
                    {car.condition === 'new' ? t('card.new') : t('card.used')}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="absolute top-4 right-4 flex gap-2">
                  <button 
                    onClick={toggleFavorite}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${isFavorite ? 'bg-red-500 text-white' : 'bg-black/50 backdrop-blur-sm text-white hover:bg-white/20'}`}
                    data-testid="favorite-btn"
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  <button 
                    onClick={handleShare}
                    className="w-10 h-10 rounded-lg bg-black/50 backdrop-blur-sm text-white hover:bg-white/20 flex items-center justify-center transition-all"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-white/20 flex items-center justify-center transition-all"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-white/20 flex items-center justify-center transition-all"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Image Dots */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-cyan-500 w-6' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Title & Price - Mobile */}
            <div className="lg:hidden">
              <h1 className="font-heading text-3xl font-bold mb-2">{car.title}</h1>
              <p className="text-3xl font-bold text-cyan-500 neon-text mb-4">{formatPrice(car.price)}</p>
            </div>

            {/* Specifications */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="font-heading text-xl font-semibold mb-6">{t('detail.specifications')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {specs.map((spec, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      {spec.icon ? (
                        <spec.icon className="w-5 h-5 text-cyan-500" />
                      ) : (
                        <Check className="w-5 h-5 text-cyan-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">{spec.label}</p>
                      <p className="font-medium">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="font-heading text-xl font-semibold mb-4">{t('detail.description')}</h2>
              <p className="text-gray-400 leading-relaxed">{car.description}</p>
            </div>

            {/* Location */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="font-heading text-xl font-semibold mb-4">{t('detail.location')}</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cyan-500" />
                </div>
                <p className="text-gray-400">{car.location}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Price Card - Desktop */}
              <div className="glass-card rounded-2xl p-6 hidden lg:block">
                <h1 className="font-heading text-2xl font-bold mb-2">{car.title}</h1>
                <p className="text-4xl font-bold text-cyan-500 neon-text mb-4">{formatPrice(car.price)}</p>
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {car.views} {t('detail.views')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {t('detail.listedRecently')}
                  </span>
                </div>
                <Button 
                  onClick={handleContact}
                  className="w-full h-14 bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-lg shadow-neon hover:shadow-neon-lg transition-all"
                  data-testid="contact-seller-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {t('detail.contactSeller')}
                </Button>
              </div>

              {/* Seller Info */}
              <div className="glass-card rounded-2xl p-6">
                <h3 className="font-heading text-lg font-semibold mb-4">{t('detail.sellerInfo')}</h3>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                    <User className="w-7 h-7 text-cyan-500" />
                  </div>
                  <div>
                    <p className="font-semibold">{car.seller_name}</p>
                    <p className="text-sm text-gray-400">{t('detail.verifiedSeller')}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-center gap-2 h-12 rounded-lg border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all">
                    <Phone className="w-4 h-4 text-cyan-500" />
                    {t('detail.showPhone')}
                  </button>
                  <button className="w-full flex items-center justify-center gap-2 h-12 rounded-lg border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all">
                    <Mail className="w-4 h-4 text-cyan-500" />
                    {t('detail.sendMessage')}
                  </button>
                </div>
              </div>

              {/* Safety Tips */}
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-cyan-500" />
                  <h3 className="font-heading text-lg font-semibold">{t('detail.safetyTips')}</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    {t('detail.tip1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    {t('detail.tip2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    {t('detail.tip3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile CTA */}
        <div className="fixed bottom-0 left-0 right-0 p-4 glass border-t border-white/10 lg:hidden z-40">
          <Button 
            onClick={handleContact}
            className="w-full h-14 bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-lg shadow-neon"
          >
            <Mail className="w-5 h-5 mr-2" />
            {t('detail.contactSeller')} - {formatPrice(car.price)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CarDetailPage;
